import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { dollars } from "@prescreen/lib/formats"
import { CreditEvaluation } from "@prescreen/lib/types/credit-evaluation"
import { Check, X, ChevronDown, ChevronUp } from "lucide-react"
import { DecisionReasons, DecisionRulesEvaluationDetails } from "./DecisionRulesEvaluation"

interface EvaluationsSectionProps {
  evaluations: CreditEvaluation[]
}

export function EvaluationsSection({ evaluations }: EvaluationsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit Evaluations ({evaluations.length})</CardTitle>
        <CardDescription>
          Loan product evaluations based on credit criteria
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {evaluations.map((evaluation, idx) => (
            <EvaluationCard key={idx} evaluation={evaluation} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function EvaluationCard({ evaluation }: { evaluation: CreditEvaluation }) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Build headline with monthly payment and postDTI
  const dti = evaluation.postDti !== undefined
    ? `${evaluation.postDti.toFixed(1)} DTI`
    : 'DTI unavailable'
  const headline = `${dollars(evaluation.estimatedPayment)}/mo ${dti}`

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1">
          <h4 className="font-medium text-lg">{headline}</h4>
          {evaluation.loanTermMonths !== undefined && evaluation.interestRate !== undefined && (
            <p className="text-sm text-gray-600 mt-1">
              {evaluation.loanTermMonths} months · {((evaluation.interestRate * 100).toFixed(2))}%
            </p>
          )}
        </div>
        <Badge 
          variant={evaluation.result === 'pass' ? 'default' : 'destructive'}
          className="text-sm px-3 py-1"
        >
          {evaluation.result === 'pass' ? 'Pass' : 'Fail'}
        </Badge>
      </div>

      {evaluation.decisionRulesEvaluation ? (
        <DecisionRulesEvaluationDetails
          evaluation={evaluation.decisionRulesEvaluation}
          reasons={evaluation.reasons}
        />
      ) : evaluation.reasons && evaluation.reasons.length > 0 ? (
        <DecisionReasons reasons={evaluation.reasons} />
      ) : null}

      {/* Primary Evaluation - Table-like format */}
      <div className="space-y-2 mb-4">
        {evaluation.statedGrossMonthlyIncome !== undefined && (
          <div className="flex justify-between items-center py-2 border-b">
            <span className="font-medium text-sm text-gray-700">Stated Monthly Income</span>
            <span className="text-sm font-semibold">{dollars(evaluation.statedGrossMonthlyIncome)}</span>
          </div>
        )}
        {evaluation.currentDti !== undefined && (
          <div className="flex justify-between items-center py-2 border-b">
            <span className="font-medium text-sm text-gray-700">Current DTI</span>
            <span className="text-sm font-semibold">{evaluation.currentDti.toFixed(1)}%</span>
          </div>
        )}
        {evaluation.postDti !== undefined && (
          <div className="flex justify-between items-center py-2 border-b">
            <span className="font-medium text-sm text-gray-700">Post DTI</span>
            <span className="text-sm font-semibold">{evaluation.postDti.toFixed(1)}%</span>
          </div>
        )}
        {evaluation.estimatedPayment !== undefined && (
          <div className="flex justify-between items-center py-2 border-b">
            <span className="font-medium text-sm text-gray-700">Estimated Payment</span>
            <span className="text-sm font-semibold">{dollars(evaluation.estimatedPayment)}</span>
          </div>
        )}
        {evaluation.maxAllowableMonthlyPayment !== undefined && (
          <div className="flex justify-between items-center py-2 border-b">
            <span className="font-medium text-sm text-gray-700">Max Allowable Monthly Payment</span>
            <span className="text-sm font-semibold">{dollars(evaluation.maxAllowableMonthlyPayment)}</span>
          </div>
        )}
      </div>

      {/* Expandable byCriteria section */}
      {evaluation.byCriteria && evaluation.byCriteria.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full justify-between mb-3"
          >
            <span className="font-medium text-sm">Criteria Evaluations ({evaluation.byCriteria.length})</span>
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          
          {isExpanded && (
            <div className="space-y-4">
              {evaluation.byCriteria.map((criteriaEval, criteriaIdx) => (
                <div key={criteriaIdx} className="border rounded-lg p-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Result</TableHead>
                        <TableHead>Min</TableHead>
                        <TableHead>DTI</TableHead>
                        <TableHead>Bankruptcy</TableHead>
                        <TableHead>Collections</TableHead>
                        <TableHead>Past Due</TableHead>
                        <TableHead>Max Payment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {criteriaEval.result === 'pass' ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <X className="h-5 w-5 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.criteria.minScore !== undefined ? criteriaEval.criteria.minScore : '—'}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.criteria.debtToIncome !== undefined 
                            ? criteriaEval.criteria.debtToIncome
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.criteria.bankruptcy 
                            ? formatCriteriaSpec(criteriaEval.criteria.bankruptcy)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.criteria.collections
                            ? formatCriteriaSpec(criteriaEval.criteria.collections)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.criteria.pastDue
                            ? formatPastDueSpec(criteriaEval.criteria.pastDue)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {criteriaEval.maxAllowableMonthlyPayment !== undefined
                            ? dollars(criteriaEval.maxAllowableMonthlyPayment)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  
                  {criteriaEval.reasons && criteriaEval.reasons.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Reasons:</span>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          {criteriaEval.reasons.map((reason, reasonIdx) => (
                            <li key={reasonIdx}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function formatCriteriaSpec(spec: any): string {
  const parts: string[] = []
  
  if (spec.none === true) {
    return 'none: true'
  }
  
  if (spec.before) {
    const dateStr = typeof spec.before === 'string' ? spec.before : spec.before.toISOString()
    parts.push(`before: ${dateStr}`)
  }
  
  if (spec.since) {
    const sinceStr = typeof spec.since === 'string' ? spec.since : spec.since.toISOString()
    parts.push(`since: ${sinceStr}`)
  }
  
  if (spec.only) {
    parts.push(`only: ${String(spec.only).padStart(2, '0')}`)
  }
  
  return parts.length > 0 ? parts.join(' ') : '—'
}

function formatPastDueSpec(spec: any): string {
  const parts: string[] = []
  if (spec.none === true) {
    return 'none: true'
  }
  if (spec.since) {
    parts.push(`since: ${spec.since}`)
  }
  if (spec.days) {
    parts.push(`days: ${spec.days}`)
  }
  if (spec.max !== undefined) {
    parts.push(`max: ${spec.max}`)
  }
  return parts.length > 0 ? parts.join(' ') : '—'
}

